import { useState } from 'react'
import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import { APIRequest } from '../../../api'
import { useThunkDispatch } from '../../../hooks/useThunkDispatch'
import { loginCookie } from '../../../store/profile'
import type { UseAuthLoginPasskeysHook } from './useAuthLoginPasskeys.types'

export const useAuthLoginPasskeys = (): UseAuthLoginPasskeysHook => {
	const [passkeyBusy, setPasskeyBusy] = useState(false)
	const [passkeyStatus, setPasskeyStatus] = useState<string | null>(null)
	const dispatchThunk = useThunkDispatch()
	const getPasskeyErrorMessage = (err: unknown): string => {
		const errorMessage = err instanceof Error ? err.message : String(err)
		const notAllowedError = errorMessage.toLowerCase().includes('not allowed')

		return notAllowedError ? 'Operation cancelled' : errorMessage
	}

	const onPasskeySignIn = async (): Promise<void> => {
		try {
			setPasskeyStatus(null)

			if (!window.PublicKeyCredential || !navigator.credentials?.get) {
				throw new Error('Passkeys are not supported in this browser.')
			}

			setPasskeyBusy(true)

			const { challengeId, options } = await APIRequest.User.webauthnLoginOptions()

			const credential = await startAuthentication(options)
			if (!credential) throw new Error('No credential returned.')

			await APIRequest.User.webauthnLoginVerify({
				challengeId,
				credentialResponse: credential
			})
			await dispatchThunk(loginCookie())
		} catch (err) {
			setPasskeyStatus(getPasskeyErrorMessage(err))
			throw err
		} finally {
			setPasskeyBusy(false)
		}
	}

	const onPasskeyRegister = async (labelFromComponent?: string): Promise<void> => {
		try {
			setPasskeyStatus(null)

			if (!window.PublicKeyCredential || !navigator.credentials?.create) throw new Error('Passkeys are not supported in this browser.')

			const label = labelFromComponent?.trim()

			setPasskeyBusy(true)

			const { challengeId, options } = await APIRequest.User.webauthnRegisterOptions()

			const credentialResponse = await startRegistration(options)
			if (!credentialResponse) throw new Error('No credential returned.')

			await APIRequest.User.webauthnRegisterVerify({
				challengeId,
				label,
				credentialResponse
			})

			setPasskeyStatus('Passkey created.')
		} catch (err) {
			setPasskeyStatus(getPasskeyErrorMessage(err))
		} finally {
			setPasskeyBusy(false)
		}
	}

	return { passkeyBusy, passkeyStatus, onPasskeySignIn, onPasskeyRegister }
}
